import React, { useState, useEffect, useRef } from "react"
import { useFieldDragController } from "../controllers/field-drag-controller"
import { XMarkIcon, Bars3Icon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import DatePicker from "aldoo-ra/DatePicker"
import _ from "lodash"

export default function DateEditor({
  onChange,
  value,
  field,
  parentValues,
  parentObject,
  showInfo = true,
  readOnly = false,
}) {
  const getInitialValue = () => {
    // Check if value is a valid date or date object
    const isValidValue = () => {
      if (value === undefined || value === "" || value === null) return false
      // Check if it's an empty object
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // For date objects, check if they have actual date properties
        if (value instanceof Date) return !isNaN(value.getTime())
        // For plain objects, check if empty or has valid date properties
        const keys = Object.keys(value)
        if (keys.length === 0) {
          console.log('[DateEditor] getInitialValue: Empty object detected, treating as null')
          return false
        }
        // Check for valid date range object
        if (value.startDate || value.endDate) {
          return value.startDate || value.endDate
        }
      }
      return true
    }

    const valid = isValidValue()
    console.log('[DateEditor] getInitialValue:', {
      value,
      valid,
      fieldId: field.id,
      returning: valid ? value : (field.multiple ? [] : null)
    })

    // If we have a valid value, use it
    if (valid) return value

    // Handle default value
    let defaultValue = field.default
    if (defaultValue !== undefined) {
      if (field.multiple) return Array.isArray(defaultValue) ? defaultValue : [defaultValue]
      return defaultValue
    }

    // Return empty array for multiple or null for single
    return field.multiple ? [] : null
  }

  const [localValue, setLocalValue] = useState(getInitialValue)
  const isInternalChange = useRef(false)

  // Unified function to update value and notify parent
  const updateValue = (newValue) => {
    console.log('[DateEditor] updateValue called:', {
      newValue,
      fieldId: field.id,
      type: typeof newValue,
      isValidDate: newValue instanceof Date,
      keys: typeof newValue === 'object' && newValue !== null ? Object.keys(newValue) : null
    })
    isInternalChange.current = true
    setLocalValue(newValue)
    onChange?.(newValue)
  }

  // Drag controller support - moved after updateValue definition
  const { handleDragStart, handleDragOver, handleDragEnd } =
    useFieldDragController(localValue, updateValue)

  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObject)
      : field.required

  // Handle external value changes
  useEffect(() => {
    if (!isInternalChange.current && !_.isEqual(value, localValue)) {
      const newValue = getInitialValue()
      setLocalValue(newValue)
    }
    isInternalChange.current = false
  }, [value, field.default, field.multiple])

  const handleValueChange = (updatedValue, index) => {
    try {
      console.log('[DateEditor] handleValueChange called:', {
        updatedValue,
        index,
        fieldId: field.id,
        multiple: field.multiple,
        type: typeof updatedValue,
        isValidDate: updatedValue instanceof Date
      })

      if (field.multiple) {
        const newValues = Array.isArray(localValue) ? [...localValue] : []

        if (index !== undefined) {
          newValues[index] = updatedValue
        } else {
          newValues.push(updatedValue)
        }

        updateValue(newValues)
      } else {
        updateValue(updatedValue)
      }
    } catch (error) {
      console.error("Error in handleValueChange:", error)
    }
  }

  const addNewDate = () => {
    if (readOnly) return
    handleValueChange({ startDate: new Date() })
  }

  const removeDate = (index) => {
    if (readOnly) return
    const updatedValues = localValue.filter((_, i) => i !== index)
    updateValue(updatedValues)
  }

  const clearAll = () => {
    if (readOnly) return
    updateValue(field.multiple ? [] : null)
  }

  const renderItem = (item, index) => (
    <div
      key={index}
      className="mt-2 gap-2 flex flex-row items-center w-full"
      draggable={!readOnly && field.multiple && localValue?.length > 1}
      onDragStart={!readOnly && field.multiple && localValue?.length > 1 ? () => handleDragStart(index) : undefined}
      onDragOver={!readOnly && field.multiple && localValue?.length > 1 ? (e) => {
        e.preventDefault()
        handleDragOver(index)
      } : undefined}
      onDragEnd={!readOnly && field.multiple && localValue?.length > 1 ? handleDragEnd : undefined}
    >
      {!readOnly && field.multiple && localValue?.length > 1 && (
        <Bars3Icon className="w-5 h-5 cursor-move text-admin_text_secondary mr-2" />
      )}

      <DatePicker
        variant="admin"
        value={item}
        onChange={(date) => handleValueChange(date, index)}
        range={field.fieldEditor === "DateRangeEditor"}
        dateFormat="DD.MM.YY"
        readOnly={readOnly}
      />

      {!readOnly && field.multiple && localValue?.length > 1 && (
        <Button
          type="outline"
          className="text-white hover:bg-admin_error rounded-full p-2"
          onClick={() => removeDate(index)}
          disabled={readOnly}
        >
          <XMarkIcon className="w-6 h-6" />
        </Button>
      )}
    </div>
  )

  return (
    <div className="date-editor">
      {Array.isArray(localValue)
        ? localValue?.map((value, index) => renderItem(value, index))
        : localValue !== null && renderItem(localValue, 0)}

      {!field.multiple && localValue === null && !readOnly && (
        <Button
          variant="admin_secondary"
          onClick={() => handleValueChange(new Date())}
          className="mt-2"
        >
          Set Date
        </Button>
      )}

      <div className="flex flex-row justify-between items-center">
        <div className="flex items-center space-x-4 mt-4">
          {field.multiple &&
            Array.isArray(localValue) &&
            localValue?.length > 0 && (
              <span className="text-sm text-admin_text">
                {localValue.length} {localValue.length === 1 ? "item" : "items"}
              </span>
            )}

          {!readOnly && field.multiple && (
            <Button
              variant="admin_secondary"
              onClick={addNewDate}
              disabled={readOnly}
            >
              Add Date
            </Button>
          )}

          {!readOnly &&
            field.multiple &&
            Array.isArray(localValue) &&
            localValue?.length > 0 && (
              <Button
                variant="admin_secondary"
                onClick={clearAll}
                disabled={readOnly}
              >
                Clear
              </Button>
            )}
        </div>
      </div>
    </div>
  )
}
